/**
 * Long-form service-detail content for /services/:slug pages.
 * Keyed by service slug (matches SERVICES in /lib/site.js).
 */
export const SERVICE_DETAILS = {
  "personal-care": {
    hero: "Dignified personal care, delivered at home.",
    lead:
      "Bathing, grooming, dressing, and mobility support — provided with the patience, privacy, and respect your loved one deserves.",
    includes: [
      "Assistance with bathing, showering, and personal hygiene",
      "Hair, skin, and nail care",
      "Dressing, grooming, and help with clothing choices",
      "Toileting and incontinence support",
      "Safe transfers, walking assistance, and fall prevention",
      "Repositioning and range-of-motion support",
    ],
    idealFor:
      "Adults recovering from surgery, living with limited mobility, or navigating conditions like arthritis, Parkinson's, or general frailty. Ideal when a family caregiver needs professional backup for the most intimate day-to-day tasks.",
    faqs: [
      {
        q: "Are your caregivers trained for hands-on personal care?",
        a: "Yes. Every caregiver providing personal care is trained in safe transfer techniques, hygiene protocols, and dignified assistance — and matched to the client's personality and comfort level.",
      },
      {
        q: "Can we start with just a few hours per week?",
        a: "Absolutely. Many families begin with two or three visits per week and expand as needs change.",
      },
    ],
  },
  "companion-care": {
    hero: "Warm companionship, on your loved one's schedule.",
    lead:
      "Conversation, favorite hobbies, walks, light errands, and meaningful company — because loneliness ages people faster than almost anything else.",
    includes: [
      "Friendly conversation and active listening",
      "Reading, puzzles, cards, and shared hobbies",
      "Walks, gentle exercise, and outings",
      "Accompaniment to social events and family visits",
      "Light errands, grocery shopping, and pharmacy stops",
      "Emotional support during transitions or grief",
    ],
    idealFor:
      "Seniors who are still largely independent but would flourish with regular, warm human company — especially after a spouse's passing, a move, or when adult children live out of town.",
    faqs: [
      {
        q: "Can we request the same companion each visit?",
        a: "Yes — consistency is central to our model. We match one or two primary caregivers to your loved one so a real relationship can form.",
      },
      {
        q: "Do companions provide light help around the home?",
        a: "Yes. Companion caregivers often help with light tidying, meal reheating, and errands, alongside genuine company.",
      },
    ],
  },
  "medication-reminders": {
    hero: "Safe, on-time medication support.",
    lead:
      "Simple, consistent medication reminders — with careful tracking so the whole family has peace of mind.",
    includes: [
      "Timely verbal reminders for every scheduled dose",
      "Set-up and check of pill organizers",
      "Simple logs of what was taken and when",
      "Coordination with family and pharmacy on refills",
      "Watchfulness for side effects or missed doses",
      "Communication with adult children on any concerns",
    ],
    idealFor:
      "Seniors managing multiple prescriptions, recovering from a hospital stay, or in early stages of memory loss — anyone whose safety depends on the right pill at the right time.",
    faqs: [
      {
        q: "Do caregivers administer medications?",
        a: "Our caregivers provide reminders and set-up support. Administration of injections or complex medications is done by a licensed nurse when needed — we can coordinate that.",
      },
      {
        q: "Will you communicate with our loved one's doctor?",
        a: "With family authorization, yes. We can be an extra set of eyes and ears for the family — flagging changes and coordinating with primary care.",
      },
    ],
  },
  "meal-preparation": {
    hero: "Fresh, nourishing meals — cooked at home.",
    lead:
      "Meals your loved one actually looks forward to, prepared in their own kitchen and tailored to their dietary needs, preferences, and doctor's orders.",
    includes: [
      "Grocery planning and shopping",
      "Preparation of breakfast, lunch, and dinner",
      "Meal portioning for later in the day or week",
      "Special diets: low-sodium, diabetic, soft-food, cardiac, and more",
      "Assistance with eating when needed",
      "Kitchen tidy-up after every meal",
    ],
    idealFor:
      "Seniors who struggle to shop, cook, or eat consistently — including those recovering from surgery, living with diabetes or heart disease, or gently losing appetite with age.",
    faqs: [
      {
        q: "Can caregivers cook to a specific diet?",
        a: "Yes. We work from your loved one's diet plan, dietary restrictions, and long-time favorite meals to prepare food they'll want to eat.",
      },
      {
        q: "Do you handle grocery shopping too?",
        a: "Absolutely. Grocery planning, shopping, and putting everything away is included when meal preparation is part of the care plan.",
      },
    ],
  },
  "light-housekeeping": {
    hero: "A tidy, safe, welcoming home.",
    lead:
      "Laundry, dishes, gentle tidying, and bed-making — the light, consistent housekeeping that keeps a home comfortable and safe.",
    includes: [
      "Laundry: wash, dry, fold, and put away",
      "Dishwashing and kitchen tidying",
      "Bed-making and light bedroom straightening",
      "Bathroom wipe-downs and safety checks",
      "Dusting and vacuuming main living areas",
      "Trash removal and mail organization",
    ],
    idealFor:
      "Seniors who love their home but find full housekeeping physically difficult. Perfect when families want a caregiver who will keep things clean and safe without being intrusive.",
    faqs: [
      {
        q: "Is this a deep-cleaning service?",
        a: "No. This is light, ongoing housekeeping alongside our care visits. For deep cleaning, we can recommend trusted local partners.",
      },
      {
        q: "Can housekeeping be combined with personal care?",
        a: "Yes — most of our clients combine light housekeeping with personal or companion care in the same visit.",
      },
    ],
  },
  transportation: {
    hero: "Reliable rides to appointments, errands, and outings.",
    lead:
      "A caring, familiar face behind the wheel — so getting to a doctor's appointment, the pharmacy, or a family lunch is easy again.",
    includes: [
      "Medical appointments (doctor, dentist, physical therapy)",
      "Pharmacy pick-ups and grocery trips",
      "Bank, hair salon, and personal errands",
      "Social outings, church, and family gatherings",
      "Accompaniment inside the appointment or store",
      "Notes from appointments shared with the family",
    ],
    idealFor:
      "Seniors who no longer drive, or families who worry about a parent driving alone. Also ideal for clients who need someone in the room during medical appointments to remember the details.",
    faqs: [
      {
        q: "Do caregivers stay during appointments?",
        a: "Yes. Our caregivers accompany your loved one inside, take notes for the family, and help ensure nothing important is missed.",
      },
      {
        q: "Are caregivers insured to drive our loved one?",
        a: "Yes. All caregivers driving clients meet our insurance and safe-driving standards.",
      },
    ],
  },
  "alzheimers-dementia": {
    hero: "Specialized Alzheimer's & dementia care, at home.",
    lead:
      "Structure, safety, and calm — delivered by caregivers specifically trained to support memory loss with patience and dignity.",
    includes: [
      "Predictable, calming daily routines",
      "Redirection and de-escalation of anxious moments",
      "Meaningful activities matched to remaining abilities",
      "Safe supervision and fall prevention",
      "Respite for family caregivers who need rest",
      "Ongoing communication with adult children and physicians",
    ],
    idealFor:
      "Families navigating any stage of Alzheimer's, vascular dementia, Lewy body dementia, or Parkinson's-related cognitive changes. Especially valuable when a spouse is the primary caregiver and needs professional support.",
    faqs: [
      {
        q: "Are your dementia caregivers specially trained?",
        a: "Yes. Our memory-care caregivers receive additional training in de-escalation, communication, and stage-appropriate engagement — and are matched carefully to each client.",
      },
      {
        q: "Can you provide overnight or 24-hour dementia care?",
        a: "Yes. Overnight, live-in, and 24/7 care are among our most-requested memory-care arrangements.",
      },
      {
        q: "How do you handle sundowning?",
        a: "We build the daily routine — light, activity, meals, and calming rituals — specifically to soften late-afternoon anxiety and reduce sundowning episodes.",
      },
    ],
  },
};
